import {
    AvailabilityHtmlId,
    isAddressError,
    isServiceError,
    JarvisMarketability,
    JarvisOption,
    JarvisProduct,
    JarvisTvProduct,
    selectMarketability,
    selectTvConnect,
} from '@vfde-sails/availability';
import { debugInfo } from '@vfde-sails/utils';
import { call, select } from 'redux-saga/effects';
import { selectGigakombiOption, selectIsGigakombi, selectProduct, selectSelected } from '../container/App/selector';
import { AppContainerId, HtmlId, HttpStatus } from '../enum';
import { AdditionalPageOptions, Selected } from '../interface';
import { getGigakombiTrackingData, getPageViewTrackingData, getTrackingAvcForm, getTrackingErrorData } from './tracking';
import { getNotificationHeadlineText } from './util';
import { isLoggedInSession } from '@vfde-sails/login';
import { PageViewTrackingData, TrackingAvcForm, TrackingErrorType, TrackingFixnetError, TrackingFormStepName, TrackingSelectedOption, trackIt, trackNotification, TrackType } from '@vfde-sails/tracking';
import { selectAvailability } from '../container/AddressValidation/selector';
import { ProductAvailability } from '../container/AddressValidation/interface';
import summaryCardUtils from '../util/component/summaryCard.utils';
import { mapStateToProps, ProductContainerProps } from '../container/Product';
import { getCMSConfig } from '../util/cms';

export function* trackPageView () {
    try {
        const product: JarvisProduct = yield select(selectProduct);
        const { type }: Selected = yield select(selectSelected);
        const tvConnect: JarvisTvProduct | undefined = yield select(selectTvConnect);
        const isLoggedIn: boolean = yield call(isLoggedInSession);

        const trackingData: PageViewTrackingData = yield call(getPageViewTrackingData, product, type, isLoggedIn, tvConnect);

        yield call(trackIt, TrackType.PageView, trackingData);
    } catch (error) {
        yield call(debugInfo, 'TrackPageView failed: ', error);
    }
}

export function* trackForm (step: TrackingFormStepName | 'outcome') {
    try {
        const product: JarvisProduct = yield select(selectProduct);
        const { type }: Selected = yield select(selectSelected);
        const isLoggedIn: boolean = yield call(isLoggedInSession);
        const marketability: JarvisMarketability | undefined = yield select(selectMarketability);
        const availability: ProductAvailability = yield select(selectAvailability);

        const trackingData: TrackingAvcForm = yield call(getTrackingAvcForm, product, type, step, isLoggedIn, availability, marketability);

        yield call(trackIt, TrackType.PageView, trackingData);
    } catch (error) {
        yield call(debugInfo, 'TrackForm failed: ', error);
    }
}

export function* trackError (notificationId: HtmlId, type = TrackingErrorType.Technical, status = HttpStatus.INTERNAL) {
    const message: string = yield call(getNotificationHeadlineText, notificationId);
    const trackingData: TrackingFixnetError = yield call(getTrackingErrorData, message, type, status);

    yield call(trackIt, TrackType.Error, trackingData);
}

export function* trackErrorHandler (error: unknown) {
    const isInvalidAddress: boolean = yield call(isAddressError, error);

    if (isInvalidAddress) {
        yield call(trackError, AvailabilityHtmlId.AddressError, TrackingErrorType.Validation, HttpStatus.BAD_REQUEST);

        return;
    }

    const isServiceUnavailable: boolean = yield call(isServiceError, error);

    if (isServiceUnavailable) {
        yield call(trackError, AvailabilityHtmlId.ServiceError);

        return;
    }

    yield call(trackError, AppContainerId.ERROR);
}

export function* trackGigakombi () {
    try {
        const option: JarvisOption | undefined = yield select(selectGigakombiOption);
        const isGigakombi: boolean = yield select(selectIsGigakombi);

        const trackingData: TrackingSelectedOption = yield call(getGigakombiTrackingData, isGigakombi, option);

        yield call(trackIt, TrackType.SwitchOptions, trackingData);
    } catch (error) {
        yield call(debugInfo, 'TrackGigakombi failed: ', error);
    }
}

export function* trackCustomerNotification () {
    try {
        const props: ProductContainerProps = yield select(mapStateToProps);
        const cmsProps: AdditionalPageOptions = yield call(getCMSConfig);

        const headline: string = yield call(summaryCardUtils.getHeadlineText, props, cmsProps.summaryCard.headline);
        const subline: string[] = yield call(summaryCardUtils.getSublineText, props, cmsProps.summaryCard.subline);
        const details: string = yield call({ context: subline, fn: 'join' }, ' ');

        yield call(trackNotification, headline, undefined, details);
    } catch (error) {
        yield call(debugInfo, 'TrackNotification failed: ', error);
    }
}
